import { sourceFormConfig } from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/source-form-info.js";

import { readerData, writerData } from "./review.consts";
import { validationUtils } from "../../utils";
import { PRESERVED_NAMES } from "core/constants";
import { convertNameToHumanRedable } from "../../../../../app/components/common/editor/control-resource-resolver";
import componentService, { componentID } from "../../../../../core/services/componentService/component-service";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import { wizardIndexedDBService } from "../../../../../core/services/indexedDB/wizard-indexedDB-service";

const removeQuotes = string => string?.replace(/^"|"$/g, "").trim();

export const getSourceTables = (adapterProps, isObject = false) => {
    let Schemas = new Set();
    let Tables = [];

    if (isObject) {
        if (adapterProps.properties.ZendeskObjects)
            return { Schemas: adapterProps.properties.ZendeskObjects.split(";"), Tables: null };
        if (adapterProps.properties.sObjects)
            return { Schemas: adapterProps.properties.sObjects.split(";"), Tables: null };
        else
            return {
                Schemas: adapterProps.properties.Tables
                    ? adapterProps.properties.Tables.split(";")
                    : adapterProps.properties.tables.split(";"),
                Tables: null
            };
    }

    if (
        adapterProps &&
        adapterProps.properties &&
        (adapterProps.properties.Tables || adapterProps.properties.collections || adapterProps.properties.Containers)
    ) {
        const tables = adapterProps.properties.Tables
            ? adapterProps.properties.Tables.split(";")
            : adapterProps.properties.collections
            ? adapterProps.properties.collections.split(",")
            : adapterProps.properties.Containers.split(",");

        if (tables) {
            tables.forEach(table => {
                let chunks = table.split(".");
                if (chunks.length === 3) {
                    chunks.shift();
                }
                const schemaName = removeQuotes(chunks[0]);
                const tableName = removeQuotes(chunks[1]);
                if (schemaName) Schemas.add(schemaName);
                Tables[schemaName] = Tables[schemaName] || [];
                Tables[schemaName].push(tableName);
            });
        }
    }
    return { Schemas: Array.from(Schemas), Tables: Tables };
};

const getPattern = database => {
    switch (database) {
        case "Oracle":
        case "OracleReader":
        case "Postgres":
        case "PostgreSQLReader":
            return /\/\/([^:/]+):(\d+)\/(\w+)/;
        case "SQLServer":
        case "MSSqlReader":
            return /\/\/(.*?):(\d+);DatabaseName=(.*)/;
        case "MySQL":
        case "MysqlReader":
        case "MariaDB":
        case "MariaDBReader":
            return /\/\/(.*?):(\d+)/;
    }
};

const getReviewData = (dataFormat: Object, database: string, databaseDetails: Object, componentID: componentID) => {
    const databaseKey = database.toUpperCase();
    if (Object.hasOwnProperty.call(dataFormat, databaseKey)) {
        return dataFormat[databaseKey]
            .filter(({ value, key }) => value || databaseDetails?.[key])
            .map(({ name, key, value }) => ({
                name: name,
                value: value || databaseDetails?.[key]
            }));
    } else {
        try {
            const propertyTemplate = componentService.getPropertyTemplate(componentID);
            const sensitivePattern = /(table|object|adapterName|ApplicationAPIReaderName)/i;
            const reviewData = propertyTemplate
                .get("propertyMap")
                .filter((property: PropertyMapItem) => {
                    if (sensitivePattern.test(property.name)) return false;
                    return databaseDetails?.[property.name];
                })
                .map((property: PropertyMapItem) => {
                    if (property.type === "com.webaction.security.Password") {
                        return {
                            name: convertNameToHumanRedable(property.name),
                            value: "*".repeat(10)
                        };
                    }
                    return {
                        name: convertNameToHumanRedable(property.name),
                        value: databaseDetails[property.name]
                    };
                });
            return reviewData;
        } catch (e) {
            console.error("Error fetching property template", e);
            const sensitivePattern = /(token|key|encrypted|secret|password|table|object|adapterName|ApplicationAPIReaderName)/i;
            const reviewData = Object.entries(databaseDetails)
                .filter(([key, value]) => !sensitivePattern.test(key) && typeof value !== "object")
                .map(([key, value]) => ({ name: convertNameToHumanRedable(key), value }));
            return reviewData;
        }
    }
};

export const getSourceDetails = (
    source,
    sourceDetails,
    sourceConnectionProfileData,
    sourceComponentID: componentID
) => {
    const resource = {
        ApiToken: "Api Token",
        ZendeskCreds: "Zendesk Creds"
    };

    const getLabel = name => resource?.[name] || name;

    sourceDetails = { ...sourceDetails, ...sourceConnectionProfileData?.connectionDetails };
    const ConnectionProfile = sourceDetails?.useConnectionProfile
        ? [
              { name: "Connection Profile", value: sourceDetails?.connectionProfileName },
              { name: "Host", value: sourceDetails?.host }
          ]
        : [];

    switch (source) {
        case "ZendeskReader":
            if (sourceDetails?.useConnectionProfile) return ConnectionProfile;
            const AuthDetails = sourceDetails?.authflow
                ? [{ name: "Auth Mode", value: "Auth Token" }]
                : [
                      { name: "Auth Mode", value: getLabel(sourceDetails?.AuthMode) },
                      { name: "Username", value: sourceDetails?.Username }
                  ];
            return [{ name: "Connection Url", value: sourceDetails?.ConnectionUrl }, ...AuthDetails];
        case "StripeReader":
            const ConnectedAccount = sourceDetails?.ConnectedAccount
                ? [{ name: "Account ID", value: sourceDetails?.AccountId }]
                : sourceDetails?.ApiKey?.encrypted
                ? [{ name: "API Key", value: `${sourceDetails?.ApiKey?.encrypted} (encrypted)` }]
                : [{ name: "Authentication Mode", value: sourceDetails?.AuthMode }];
            return [...ConnectedAccount];
        case "SalesForceReader":
        case "SalesforceCDCReader":
            if (sourceDetails?.useConnectionProfile) return ConnectionProfile;
            else return [{ name: "API Endpoint", value: sourceDetails?.apiEndPoint }];
        case "ServiceNowReader":
            if (sourceDetails?.useConnectionProfile) return ConnectionProfile;
            else return getReviewData(readerData, source, sourceDetails);
        case "MicrosoftDynamics365Reader":
            if (sourceDetails?.useConnectionProfile)
                return [
                    ...ConnectionProfile,
                    { name: "Edition", value: sourceDetails?.Edition },
                    ...(sourceDetails?.AdfsServer && [{ name: "Adfs Server", value: sourceDetails?.AdfsServer }])
                ];
            else return getReviewData(readerData, source, sourceDetails);
        case "MicrosoftDynamics365BusinessCentralReader":
            if (sourceDetails?.useConnectionProfile)
                return [...ConnectionProfile, { name: "Company", value: sourceDetails?.Company }];
            else return getReviewData(readerData, source, sourceDetails);
        // To-do Add details for other targets
        case "Oracle":
        case "MySQL":
        case "MariaDB":
        case "MariaDBXpand":
        case "Postgres":
        case "SQLServer":
        case "OracleReader":
        case "PostgreSQLReader":
        case "MysqlReader":
        case "MariaDBReader":
        case "MariaDBXpandReader":
        case "MSSqlReader":
        case "OJet":
        case "MSJet":
            if (sourceDetails?.useConnectionProfile) return ConnectionProfile;
            else return getReviewData(readerData, source, sourceDetails);
        default:
            return getReviewData(readerData, source, sourceDetails, sourceComponentID);
    }
};

export const getTargetDetails = (target, targetDetails, connectionProfileData, targetComponentID: componentID) => {
    targetDetails = { ...targetDetails, ...connectionProfileData?.connectionDetails };
    const ConnectionProfile = targetDetails?.useConnectionProfile
        ? [{ name: "Connection Profile", value: targetDetails?.connectionProfileName }]
        : [];

    switch (target) {
        case "Snowflake":
            return [
                ...ConnectionProfile,
                { name: "Authentication type", value: targetDetails?.authenticationType },
                { name: "Connection URL", value: targetDetails?.connectionUrl },
                { name: "Username", value: targetDetails?.username },
                { name: "Database", value: targetDetails?.dbName }
            ];
        case "BigQuery":
            return [
                ...ConnectionProfile,
                ConnectionProfile.length && { name: "Connection URL", value: targetDetails?.ConnectionUrl },
                { name: "Project ID", value: targetDetails?.projectId },
                { name: "Billing Project ID", value: targetDetails?.BillingProjectId },
                {
                    name: "Private Service Connect Endpoint",
                    value: targetDetails?.PrivateServiceConnectEndpoint
                },
                { name: "Dataset", value: targetDetails?.dataset }
            ];
        case "Databricks":
            return [
                ...ConnectionProfile,
                { name: "Connection URL", value: targetDetails?.connectionUrl },
                {
                    name: "Catalog Name",
                    value: targetDetails?.catalog
                }
            ];
        case "Fabric Data Warehouse":
            return [
                ...ConnectionProfile,
                ConnectionProfile.length && {
                    name: "External Stage Connection Profile",
                    value: targetDetails?.externalStageConnectionProfileName
                },
                {
                    name: "Connection URL",
                    value: targetDetails?.connectionUrl
                },
                { name: "Warehouse Name", value: targetDetails?.warehouseName },
                { name: "Username", value: targetDetails?.username },
                { name: "Azure Account Name", value: targetDetails?.accountName }
            ];

        case "Iceberg":
            return [
                { name: "DataLake Type", value: targetDetails?.DataLakeType },
                { name: "DataLake Connection Profile Name", value: targetDetails?.DataLakeConnectionProfileName },
                { name: "Iceberg Tables Location", value: targetDetails?.IcebergTablesLocation },
                { name: "Compute Type", value: targetDetails?.ComputeType },
                { name: "Compute Connection Profile Name", value: targetDetails?.ComputeConnectionProfileName },
                { name: "Catalog Type", value: targetDetails?.CatalogType },
                { name: "Catalog Connection Profile Name", value: targetDetails?.CatalogConnectionProfileName },
                { name: "External Staging Type", value: targetDetails?.ExternalStagingType },
                { name: "External Staging Location", value: targetDetails?.ExternalStagingLocation }
            ];
        case "Google Cloud Lakehouse":
            return [
                { name: "DataLake Connection Profile Name", value: targetDetails?.DataLakeConnectionProfileName },
                { name: "Iceberg Tables Location", value: targetDetails?.IcebergTablesLocation },
                { name: "Compute Connection Profile Name", value: targetDetails?.ComputeConnectionProfileName },
                { name: "Catalog Connection Profile Name", value: targetDetails?.CatalogConnectionProfileName },
                { name: "External Staging Location", value: targetDetails?.ExternalStagingLocation },
                {
                    name: "External Stage Connection Profile Name",
                    value: targetDetails?.ExternalStageConnectionProfileName
                }
            ];
        case "S3 Tables":
            return [
                { name: "DataLake Connection Profile Name", value: targetDetails?.DataLakeConnectionProfileName },
                { name: "Compute Connection Profile Name", value: targetDetails?.ComputeConnectionProfileName },
                { name: "External Staging Location", value: targetDetails?.ExternalStagingLocation },
                {
                    name: "External Staging Connection Profile Name",
                    value: targetDetails?.ExternalStageConnectionProfileName
                }
            ];
        case "ClickHouse":
            return [{ name: "Connection Profile", value: targetDetails?.connectionProfileName }];
        // To-do Add details for other targets
        case "Fabric Mirrored database":
            return [
                { name: "Mirror Database Name", value: targetDetails?.MirroredDatabaseName },
                { name: "Connection Profile Name", value: targetDetails?.connectionProfileName },
                { name: "Authentication type", value: PRESERVED_NAMES[targetDetails?.authenticationType] },
                { name: "Username", value: targetDetails?.Username },
                { name: "Workspace Entity", value: targetDetails?.workspaceEntity }
            ];
        case "Oracle Database":
        case "PostgreSQL":
        case "MySQL":
        case "SQL Server Database":
        case "MariaDB":
            if (targetDetails?.useConnectionProfile) return ConnectionProfile;
            else return getReviewData(writerData, target, targetDetails, targetComponentID);
        default:
            return getReviewData(writerData, target, targetDetails, targetComponentID);
    }
};
export const getStagingDetails = (target, targetDetails) => {
    switch (target) {
        case "Fabric Data Warehouse":
            return [{ name: "Azure Account Name", value: targetDetails?.accountName }];
        default:
            return null;
    }
};

export const getAdvancedDetails = (target, targetDetails, targetConnectionProfileData) => {
    const resource = {
        Process: "Automatically propagate schema changes to the target (Process)",
        Ignore: "Do not propagate schema changes and keep the pipeline running (Ignore)",
        Halt: "Pause the pipeline when the schema changes (Halt)",
        MERGE: "Directly (Merge)",
        APPENDONLY: "As audit records (Append)",
        aws: "Amazon S3",
        az: "Azure ADLSGen2",
        DatabricksFileSystem: "Databricks File System",
        PSL: "Personal Staging Location",
        S3: "Amazon S3",
        ADLSGen2: "Azure ADLSGen2",
        GCS: "Google Cloud Storage",
        PersonalStagingLocation: "Personal Staging Username",
        DBFSROOT: "Databricks File System",
        MergeTree: "MergeTree",
        ReplacingMergeTree: "ReplacingMergeTree",
        CoalescingMergeTree: "CoalescingMergeTree",
        CollapsingMergeTree: "CollapsingMergeTree"
    };

    const externalStageDetails = targetConnectionProfileData?.externalStageDetails ?? targetDetails;

    const getLabel = name => resource?.[name] || name;

    const Details = {
        CDDLAction: { name: "When schema changes in the source database", value: getLabel(targetDetails?.CDDLAction) },
        Mode: { name: `Write continous changes to ${target}`, value: getLabel(targetDetails?.Mode) },
        TableEngine: {
            name: "ClickHouse Table Engine for continuous changes",
            value: getLabel(targetDetails?.TableEngine)
        },
        ParallelThreads: {
            name: "Number of parallel thread to use during initial load",
            value:
                getLabel(targetDetails?.ParallelThreads) === ""
                    ? "1 (Do not use parallelism)"
                    : getLabel(targetDetails?.ParallelThreads)
        },
        CDDLTrackingTable: {
            name: "CDDL Tracking table",
            value: getLabel(targetDetails?.CDDLTrackingTable)
        },
        CSP: {
            name: "External Staging Type",
            value: getLabel(targetDetails?.externalStageType),
            isSubComponent: true
        },
        StagingCP: {
            name: "External Staging Connection Profile",
            value: getLabel(targetDetails?.externalStageConnectionProfileName),
            isSubComponent: true
        },
        StreamingConfig: {
            name: "Streaming configuration",
            value: getLabel(targetDetails?.StreamingConfiguration),
            isSubComponent: true
        },
        UserRole: {
            name: "User Role",
            value: getLabel(targetDetails?.userRole),
            isSubComponent: true
        },
        UploadType: {
            name: `Mode to use for writing continuous changes to ${target}`,
            value: getLabel(targetDetails?.streamingUpload ? "Snowpipe streaming" : "File upload")
        }
    };

    const getStageDetails = (targetDetails, stageDetails, showAsSubComponent = false) => {
        const stagingCP = targetDetails?.externalStageConnectionProfileName ? [Details.StagingCP] : [];
        const type = targetDetails?.externalStageType;
        switch (type) {
            case "S3":
                return [
                    ...stagingCP,
                    { name: "S3 Region", value: getLabel(stageDetails.s3Region), isSubComponent: showAsSubComponent },
                    {
                        name: "S3 Bucket Name",
                        value: getLabel(stageDetails.s3BucketName),
                        isSubComponent: showAsSubComponent
                    }
                ];
            case "ADLSGen2":
                return [
                    ...stagingCP,
                    {
                        name: "Azure Account Name",
                        value: getLabel(stageDetails.azureAccountName),
                        isSubComponent: showAsSubComponent
                    },
                    {
                        name: "Azure Container Name",
                        value: getLabel(stageDetails.azureContainerName),
                        isSubComponent: showAsSubComponent
                    }
                ];
            case "GCS":
                return [
                    ...stagingCP,
                    {
                        name: "Storage Credential Name",
                        value: getLabel(stageDetails.storageCredentialName),
                        isSubComponent: showAsSubComponent
                    },
                    {
                        name: "GCS Bucket Name",
                        value: getLabel(stageDetails.gcsBucketName),
                        isSubComponent: showAsSubComponent
                    }
                ];
            case "PersonalStagingLocation":
                return [
                    {
                        name: "Personal Staging Username",
                        value: getLabel(stageDetails.personalStagingUsername),
                        isSubComponent: showAsSubComponent
                    }
                ];
            default:
                return [];
        }
    };

    const getStreamingUploadDetails = targetDetails => {
        return targetDetails?.streamingUpload ? [Details.StreamingConfig, Details.UserRole] : [];
    };

    const TrackingTable = targetDetails?.CDDLTrackingTable ? Details.CDDLTrackingTable : {};

    switch (target) {
        case "Snowflake":
            return [
                Details.CDDLAction,
                TrackingTable,
                Details.Mode,
                Details.UploadType,
                Details.ParallelThreads,
                ...getStreamingUploadDetails(targetDetails),
                ...(targetDetails?.externalStageType && !targetDetails?.streamingUpload
                    ? [Details.CSP, ...getStageDetails(targetDetails, externalStageDetails, true)]
                    : [])
            ];
        case "BigQuery":
            return [
                Details.ParallelThreads,
                Details.CDDLAction,
                TrackingTable,
                Details.Mode,
                {
                    name: `Mode to use for writing continuous changes to ${target}`,
                    value:
                        typeof targetDetails?.streamingUpload === "boolean"
                            ? getLabel(targetDetails?.streamingUpload ? "Streaming Upload" : "File upload")
                            : getLabel(targetDetails?.streamingUpload === "true" ? "Streaming Upload" : "File upload")
                },
                ...getStreamingUploadDetails(targetDetails),
                ...(targetDetails?.externalStageType
                    ? [Details.CSP, ...getStageDetails(targetDetails, externalStageDetails, true)]
                    : [])
            ];
        case "Databricks":
            return [
                Details.ParallelThreads,
                Details.CDDLAction,
                TrackingTable,
                Details.Mode,
                Details.CSP,
                ...[...getStageDetails(targetDetails, externalStageDetails, true)]
            ];
        case "Fabric Data Warehouse":
            return [Details.ParallelThreads, Details.CDDLAction, TrackingTable, Details.Mode];
        case "Fabric Mirrored database":
            return [
                { name: "Batch Policy", value: targetDetails?.batchPolicy },
                Details.ParallelThreads,
                Details.CDDLAction,
                TrackingTable
            ];
        case "Iceberg":
            return [
                {
                    name: "External Stage Connection Profile Name",
                    value: targetDetails?.ExternalStageConnectionProfileName
                },
                Details.CDDLAction,
                TrackingTable,
                Details.Mode
            ];
        case "Google Cloud Lakehouse":
            return [Details.CDDLAction, Details.Mode];
        case "S3 Tables":
            return [Details.CDDLAction, Details.Mode];
        case "ClickHouse": {
            return [
                Details.TableEngine,
                ...(targetDetails?.TableEngine === "MergeTree" ? [Details.Mode] : []),
                Details.CDDLAction,
                TrackingTable
            ];
        }
        default:
            return [Details.CDDLAction, TrackingTable, Details.ParallelThreads];
    }
};

const getFileName = path => path.split("/").pop();

const formatValue = (value, isFile, options) => {
    if (!value) return "";
    if (options) return options.find(option => option.value === value).label;
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return isFile ? getFileName(value) : value;
};

export const getSSLDetails = async (source: string, target: string, appName: string): Promise<[any[], any[]]> => {
    const sourceConfig = await getSourceConfig(appName, source);
    const targetConfig = await getTargetConfig(appName, source);
    if (sourceConfig && targetConfig) {
        const sourceSSLFields = sourceFormConfig.getReviewFields(source.toUpperCase());
        const targetSSLFields = sourceFormConfig.getReviewFields(target.toUpperCase());

        const populateSSLData = (fields, config) =>
            fields
                ?.map(({ label, name, isFile, options }) => ({
                    name: label,
                    value: formatValue(config[name], isFile, options)
                }))
                .filter(item => item.value);

        return [
            populateSSLData(sourceSSLFields, sourceConfig) ?? [],
            populateSSLData(targetSSLFields, targetConfig) ?? []
        ];
    } else return [[], []];
};

export const getSourceConfig = async (AppName, sourceType) => {
    if (validationUtils.isRDBMS(sourceType.toUpperCase())) {
        const entireWizardData = await wizardIndexedDBService.getDataById(AppName);
        const sourceConfigData = entireWizardData[metaStoreService.entities.SOURCE].connectionParams;
        return sourceConfigData;
    }
    return null;
};

export const getTargetConfig = async (AppName, targetType) => {
    if (validationUtils.isRDBMS(targetType.toUpperCase())) {
        const entireWizardData = await wizardIndexedDBService.getDataById(AppName);
        const targetConfigData = entireWizardData[metaStoreService.entities.TARGET].targetInfo.targetFormValues;
        return targetConfigData;
    }
    return null;
};
